import React, { useEffect, useState, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Upload, Trash2, Image, Check, X, Copy } from 'lucide-react';

const Media = () => {
  const { API } = useApp();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [media, setMedia] = useState([]);
  const [settings, setSettings] = useState({});
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMedia();
    fetchSettings();
  }, [API]);

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/admin/media`, { withCredentials: true });
      setMedia(response.data.media || []);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, { withCredentials: true });
      setSettings(response.data || {});
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPEG, PNG, WebP, GIF');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max size: 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/media/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded successfully');
      fetchMedia();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteMedia = async (filename) => {
    if (!window.confirm('Delete this image?')) return;

    try {
      await axios.delete(`${API}/admin/media/${filename}`, { withCredentials: true });
      setMedia(prev => prev.filter(m => m.filename !== filename));
      toast.success('Image deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const setAsHeroImage = async (url) => {
    try {
      await axios.put(`${API}/admin/settings`, { hero_image: url }, { withCredentials: true });
      setSettings(prev => ({ ...prev, hero_image: url }));
      toast.success('Hero image updated');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const setAsAboutImage = async (url) => {
    try {
      await axios.put(`${API}/admin/settings`, { about_image: url }, { withCredentials: true });
      setSettings(prev => ({ ...prev, about_image: url }));
      toast.success('About image updated');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const copyUrl = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copied to clipboard');
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-media">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-syne font-bold text-2xl text-white">Media Manager</h1>
          <p className="text-white/50">Upload and manage images for your website</p>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleUpload}
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-brand-red hover:bg-brand-red-light"
            data-testid="upload-media-btn"
          >
            {uploading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Upload Image
          </Button>
        </div>
      </div>

      {/* Current Assignments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-brand-dark border-white/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white/70">Current Hero Image</CardTitle>
          </CardHeader>
          <CardContent>
            {settings.hero_image ? (
              <div className="relative aspect-video rounded overflow-hidden">
                <img src={settings.hero_image} alt="Hero" className="w-full h-full object-cover" />
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setAsHeroImage(null)}
                  className="absolute top-2 right-2 bg-black/50 hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="aspect-video bg-brand-charcoal rounded flex items-center justify-center">
                <p className="text-white/30 text-sm">No hero image set</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-brand-dark border-white/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-white/70">Current About Image</CardTitle>
          </CardHeader>
          <CardContent>
            {settings.about_image ? (
              <div className="relative aspect-video rounded overflow-hidden">
                <img src={settings.about_image} alt="About" className="w-full h-full object-cover" />
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => setAsAboutImage(null)}
                  className="absolute top-2 right-2 bg-black/50 hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="aspect-video bg-brand-charcoal rounded flex items-center justify-center">
                <p className="text-white/30 text-sm">No about image set</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Media Grid */}
      <Card className="bg-brand-dark border-white/5">
        <CardHeader>
          <CardTitle className="font-syne text-white">Uploaded Images</CardTitle>
          <CardDescription className="text-white/50">
            Click on an image to assign it to a section
          </CardDescription>
        </CardHeader>
        <CardContent>
          {media.length === 0 ? (
            <div className="text-center py-12">
              <Image className="w-12 h-12 mx-auto mb-4 text-white/20" />
              <p className="text-white/50">No images uploaded yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {media.map(item => (
                <div 
                  key={item.id} 
                  className="group relative aspect-square bg-brand-charcoal rounded-lg overflow-hidden border border-white/5 hover:border-brand-gold/50 transition-colors"
                >
                  <img
                    src={item.url}
                    alt={item.original_name}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                    <p className="text-white text-xs text-center truncate w-full">{item.original_name}</p>
                    <p className="text-white/50 text-xs">{formatSize(item.size)}</p>
                    
                    <div className="flex flex-wrap justify-center gap-1 mt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAsHeroImage(item.url)}
                        className="text-xs h-7 px-2 border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-white"
                      >
                        Hero
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAsAboutImage(item.url)}
                        className="text-xs h-7 px-2 border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-white"
                      >
                        About
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyUrl(item.url)}
                        className="text-xs h-7 px-2 border-white/20 text-white/70 hover:bg-white/10"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteMedia(item.filename)}
                        className="text-xs h-7 px-2 bg-red-600/50 hover:bg-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Assigned indicator */}
                  {(settings.hero_image === item.url || settings.about_image === item.url) && (
                    <div className="absolute top-2 right-2 px-2 py-1 bg-brand-gold rounded text-xs text-white flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      {settings.hero_image === item.url ? 'Hero' : 'About'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Media;
